import { useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { Link } from "wouter";
import { optIntoGuestBrowsing } from "@/hooks/useGuestBrowsing";
import { Button } from "@/components/ui/button";
import { Leaf, Users, MapPin, ShieldCheck, Mail, ChevronDown } from "lucide-react";
import { StoopMark } from "@/components/icons/StoopMark";
import { startGoogleOAuthMobile } from "@/lib/native";
import { useHideGoogleOnIos } from "@/hooks/useHideGoogleOnIos";

interface ProvidersResponse {
  google?: boolean;
}

export default function Landing() {
  const { data: providers } = useQuery<ProvidersResponse>({
    queryKey: ["/api/auth/providers"],
    queryFn: async () => {
      const res = await fetch("/api/auth/providers", { credentials: "include" });
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 60_000,
  });

  const hideGoogle = useHideGoogleOnIos();

  const handleGoogle = () => {
    if (Capacitor.isNativePlatform()) {
      void startGoogleOAuthMobile();
    } else {
      window.location.href = "/api/auth/google";
    }
  };

  const scrollToAbout = () => {
    const target = document.getElementById("about-section");
    target?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Splash / Login — fills viewport height */}
      <section
        className="relative flex min-h-[100svh] flex-col items-center justify-center bg-primary px-6 py-10 text-primary-foreground"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 32px)" }}
      >
        {/* Brand mark */}
        <div className="mb-12 flex flex-col items-center">
          <StoopMark className="mb-3 h-24 w-24 text-primary-foreground" />
          <h1
            className="font-display font-black tracking-wider text-primary-foreground"
            style={{
              fontSize: "clamp(4rem, 18vw, 7rem)",
              fontVariationSettings: "'opsz' 144",
              lineHeight: 1,
            }}
            data-testid="text-brand-mark"
          >
            stoop
          </h1>
          <p className="mt-4 max-w-xs text-center text-base text-primary-foreground/80">
            From their stoop to yours.
          </p>
        </div>

        {/* Login stack */}
        <div className="w-full max-w-sm space-y-3">
          {providers?.google && !hideGoogle && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={handleGoogle}
              className="w-full justify-center gap-3 border-transparent bg-background text-foreground hover:bg-background/90"
              data-testid="button-google-login"
            >
              <GoogleIcon />
              Continue with Google
            </Button>
          )}
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full justify-center gap-3 border-transparent bg-background text-foreground hover:bg-background/90"
            data-testid="button-email-login"
          >
            <Link href="/login">
              <Mail className="h-4 w-4" />
              Continue with email
            </Link>
          </Button>

          <div className="pt-2 text-center text-sm text-primary-foreground/80">
            New here?{" "}
            <Link
              href="/register"
              className="font-semibold text-primary-foreground underline underline-offset-2"
              data-testid="link-register"
            >
              Create an account
            </Link>
          </div>

          <div className="text-center text-sm">
            <button
              type="button"
              onClick={() => {
                optIntoGuestBrowsing();
                window.location.assign("/");
              }}
              className="font-semibold text-primary-foreground underline underline-offset-2"
              data-testid="link-browse-guest"
            >
              Browse as guest
            </button>
          </div>
        </div>

        {/* Scroll-to-about indicator */}
        <button
          type="button"
          onClick={scrollToAbout}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-xs font-medium text-primary-foreground/70 transition hover:text-primary-foreground"
          aria-label="Learn more"
        >
          <span>What is Stoop?</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </button>
      </section>

      {/* About / Features */}
      <section id="about-section" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2
            className="mb-12 text-center font-display text-3xl font-bold md:text-4xl"
            data-testid="text-features-title"
          >
            How It Works
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="p-6 text-center" data-testid="feature-browse">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Leaf className="h-8 w-8" />
              </div>
              <h3 className="mb-3 text-xl font-semibold">Browse local makers</h3>
              <p className="leading-relaxed text-muted-foreground">
                Discover fresh produce, baked goods, and artisan products from neighbors on your block.
              </p>
            </div>

            <div className="p-6 text-center" data-testid="feature-connect">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-secondary/15 text-brand-secondary">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="mb-3 text-xl font-semibold">Support your neighbors</h3>
              <p className="leading-relaxed text-muted-foreground">
                Connect with growers and makers near you. Strengthen the community economy.
              </p>
            </div>

            <div className="p-6 text-center" data-testid="feature-pickup">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MapPin className="h-8 w-8" />
              </div>
              <h3 className="mb-3 text-xl font-semibold">Pickup or delivery</h3>
              <p className="leading-relaxed text-muted-foreground">
                Pick up at their stoop or get it brought to yours. You choose how it arrives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-muted/30 px-4 py-16">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col items-center gap-8 md:flex-row">
            <div className="flex-1">
              <div className="mb-4 inline-flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-primary" />
                <span className="text-sm font-semibold uppercase tracking-wide text-primary">
                  Transparent & fair
                </span>
              </div>
              <h2 className="mb-4 font-display text-3xl font-bold">
                Makers keep up to 100% of their price
              </h2>
              <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
                We believe in fair pay. Founding makers pay 0% for their first 6 months; after
                that it's just a 1% card fee. Buyers cover a small service fee that drops as
                orders grow. No hidden charges.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Secure payments</p>
                    <p className="text-sm text-muted-foreground">
                      Powered by Stripe for safe transactions
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-secondary/15">
                    <div className="h-2 w-2 rounded-full bg-brand-secondary" />
                  </div>
                  <div>
                    <p className="font-medium">Local first</p>
                    <p className="text-sm text-muted-foreground">
                      Shop by ZIP code to find neighbors nearby
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <img
                src="https://images.unsplash.com/photo-1542838132-92c53300491e?q=80&w=800&auto=format&fit=crop"
                alt="Local market produce"
                className="h-[300px] w-full rounded-xl object-cover shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t px-4 py-8">
        <div className="container mx-auto max-w-6xl text-center text-sm text-muted-foreground">
          <p>Pickup at Troy Atrium, 49 4th St, Troy, NY • Saturdays 10:00–12:00</p>
          <p className="mt-2">
            Buyers pay a small service fee that drops as orders grow • Growers and makers keep the price they list
          </p>
        </div>
      </footer>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
